<template>
  <component :is="isMinimized ? 'div' : 'v-row'">
    <div class="session-qr-min__controls">
      <h4 class="print-qr-epro-title">
        {{ $ucfirst($t('printQRCodeEPROHeader',
          {
            params: {
              visitNumber: versionNumber
            }
          }
      )) }}
      </h4>

      <v-text-field
        v-if="isMinimized"
        :value="link"
        filled
        placeholder="Tablet Link"
        flat
        solo
        readonly
        hide-details
        class="mb-3"
        @click="copyToClipboard"
      ></v-text-field>
      <v-btn
        class="align-self-start btn-wrap print-qr-epro-button"
        plain
        text
        x-large
        color="primary"
        @click="printQRCode"
      >
        <v-icon class="patient-overview__icon">mdi-printer</v-icon>
        {{ $ucfirst($t('printQRCodeEPRO')) }}
      </v-btn>
    </div>
    <vue-qrcode
      class="session-qr-min__code"
      id="qr-code"
      :value="link"
      v-if="isMinimized"
    />
    <v-col v-if="!isMinimized">
      <div v-if="link">
        <vue-qrcode :value="link"/>
        <v-text-field
          :value="link"
          filled
          placeholder="Tablet Link"
          flat
          solo
          readonly
          @click="copyToClipboard"
        ></v-text-field>
      </div>
      <v-select
        v-model="chosenVersionNumber"
        :items="versionNumbers"
        label="Version"
        v-if="controls"
        outlined
      ></v-select>
      <v-select
        v-model="chosenBeforeAfter"
        :items="beforeAfters"
        label="Before Or After SDM"
        v-if="controls"
        outlined
      ></v-select>
    </v-col>
  </component>
</template>

<script>
import VueQrcode from 'vue-qrcode'
import gql from "graphql-tag";

/**
 * Component TabletSessionQR.vue
 *
 * @module TabletSessionQR
 * @date
 * @author
 * @copyright UniWeb bvba 2022
 *
 * @link https://gitlab.uniweb.be/cor/4.04-caregiver-dashboard
 *
 * @coropreventionSW [SW-4.3] app/src/APP_DASHBOARD/templates/components/Questionnaire/TabletSessionQR.vue
 * @see https://docs.google.com/document/d/1iOpHH151s_ABr-DniJIc__q-GHpXJJVq/edit#heading=h.a0z8d9xd3xr7
 *
 */

export default {
  name: 'TabletSessionQR',
  components: {
    VueQrcode
  },
  props: {
    subjectId: {
      type: String,
    },
    userName: {
      type: String,
    },
    locale: {
      type: String,
    },
    versionNumber: {
      type: Number,
    },
    afterSDM: {
      type: Boolean,
      default: () => false,
    },
    controls: {
      type: Boolean,
      default: () => true,
    },
    isMinimized: {
      type: Boolean,
      default: () => false,
    }
  },
  created() {
    this.chosenVersionNumber = this.versionNumber ? this.versionNumber : 1
    this.chosenBeforeAfter = this.afterSDM ? 'after' : 'before'
    this.chosenSubjectId = this.subjectId
    this.chosenUsername = this.userName
    this.chosenLocale = this.locale ? this.locale : 'en'
  },
  watch: {
    subjectId: function (value) {
      this.chosenSubjectId = value
    },
    userName: function (value) {
      this.chosenUsername = value
    },
    locale: function (value) {
      this.chosenLocale = value
    },
    versionNumber: function (value) {
      this.chosenVersionNumber = value ? value : 1
    },
    afterSDM: function (value) {
      this.chosenBeforeAfter = value ? 'after' : 'before'
    },
    chosenVersionNumber: function () {
      this.loadUrlAndQRCode()
    },
    chosenBeforeAfter: function () {
      this.loadUrlAndQRCode()
    },
    chosenSubjectId: function () {
      this.loadUrlAndQRCode()
    },
    chosenUsername: function () {
      this.loadUrlAndQRCode()
    },
    chosenLocale: function () {
      this.loadUrlAndQRCode()
    },
  },
  methods: {
    copyToClipboard() {
      const input = this.$el.querySelector('input');

      if (!input) return;

      try {
        input.focus();
        input.select();
        document.execCommand('copy');

        this.$toasted.show(this.$t('successMessages.copiedToClipboard'), {
          type: 'success',
          icon: 'check',
          position: 'bottom-center',
        });
      } catch (error) {
        this.$toasted.show(this.$t('error.couldNotCopyToClipboard'), {
          type: 'error',
          icon: 'error_outline',
          position: 'bottom-center',
        });
      } finally {
        input.selectionEnd = input.selectionStart;
        input.blur();
      }
    },
    printQRCode() {
      const { src } = document.getElementById('qr-code');
      const iframe = `<iframe width="100%" height="100%" src="${src}"></iframe>`;
      const isFirefox = navigator.userAgent
        .toLowerCase().indexOf('firefox') > -1;

      if (!src) return;

      const payload = isFirefox ? src : '';
      const popup = window.open(payload);

      function closePopup () {
        if (popup) {
          popup.close();
        }
      }

      popup.onbeforeunload = closePopup;
      popup.onafterprint = closePopup;

      setTimeout(() => {
        if (!isFirefox) {
          popup.document.write(iframe);
        } else {
          alert()
        }

        popup.focus();
        popup.print();
      }, 100);
    },
    loadUrlAndQRCode(){
      this.$nextTick(() => {
        if (this.loadingUrl) return
        this.loadingUrl = true

        this.$app.apolloClient
          .query({
            query: gql`
            query createTabletUrlForStartingASession(
                $subjectId: String!
                $version: Int!
                $locale: String
                $userName: String!
                $afterMeeting: Boolean!
                $initializer: String!
            ) {
                 createTabletUrlForStartingASession(
                    subjectId : $subjectId
                    version : $version
                    locale : $locale
                    userName: $userName
                    afterMeeting: $afterMeeting
                    initializer: $initializer
                )
            }
          `,
            variables: {
              subjectId: this.chosenSubjectId,
              version: this.chosenVersionNumber,
              locale: null,
              userName: this.chosenUsername ? this.chosenUsername : '',
              afterMeeting: this.chosenBeforeAfter === 'after',
              initializer: 'PATIENT'
            },
          })
          .then((result) => {
            this.link = result.data.createTabletUrlForStartingASession
            this.loadingUrl = false
          })
      });
    }
  },
  data: () => {
    return {
      loadingUrl : false,
      chosenVersionNumber: null,
      chosenBeforeAfter: null,
      chosenSubjectId: null,
      chosenUsername: null,
      chosenLocale: null,
      link: ' ',
      versionNumbers: [1, 2, 3, 4, 5, 6, 7],
      beforeAfters: ['before', 'after']
    }
  }
}
</script>

<style lang="scss">
.session-qr-min__controls {
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  flex-grow: 1;
}

.session-qr-min__code {
  border: 1px solid #ccc;
  border-radius: 6px;
  width: 180px;
  height: 180px;
  margin-left: 24px;
}

.print-qr-epro-button {
  width: 100%;
}

.print-qr-epro-title{
  padding-left: 10px;
  margin-bottom: 15px;
  font-size: 1.1em;
}
</style>
